/**
 * Add Stock Stepper
 * Generic 4-step stepper for adding stock only
 * Works for bottles, caps, labels, and supplies via config
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, Box, Typography, IconButton,
  Stepper, Step, StepLabel, StepContent, Button, CircularProgress,
  useTheme, alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getInventoryConfig } from '../../shared/inventoryConfig';
import { hasAddConfig, getAddConfig } from '../addConfigs';
import { supabase } from '../../../../../supabaseClient';
import StepSelectItem from './StepSelectItem';
import StepConfigureQuantity from './StepConfigureQuantity';
import StepProductQuantity from './StepProductQuantity';
import StepUploadDocuments from './StepUploadDocuments';
import StepReview from './StepReview';
import { submitAddStock } from './stepperHelpers';

export default function AddInventoryStepper({
  open,
  onClose,
  onBack,
  inventoryType,
  companyId,
  items,
  locations,
  suppliers,
  onSuccess
}) {
  const theme = useTheme();
  const config = getInventoryConfig(inventoryType);

  const [activeStep, setActiveStep] = useState(0);
  // Add
  const adjustmentType = 'add';
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const isProduct = config.hasUnitConfig;
  const [formData, setFormData] = useState({
    quantity: '',
    lotNumber: '',
    batchNumber: '',
    vendor: null,
    invoiceNumber: '',
    invoiceDate: '',
    poNumber: '',
    poDate: '',
    unitCost: '',
    expiryDate: '',
    mfgDate: '',
    palletQty: '',
    caseQty: '',
    unitQty: '',
    shelfLife: '',
    notes: '',
    lots: [{ palletQty: '', caseQty: '', unitQty: '', lotNumber: '', batchNumber: '', mfgDate: '', expiryDate: '' }],
    nonProductLots: [{ quantity: '', lotNumber: '', batchNumber: '', unitCost: '', expiryDate: '' }]
  });
  const [documents, setDocuments] = useState({});
  const [itemsConfig, setItemsConfig] = useState({}); // For per-item configuration
  const [submitting, setSubmitting] = useState(false);

  const steps = [
    `Select ${config.displayName.slice(0, -1)} & Location`,
    'Configure Quantities',
    'Upload Documents',
    'Review & Submit'
  ];


  // Auto-fill unit_cost when items are selected
  useEffect(() => {
    if (selectedItems.length === 1 && !isProduct) {
      // Single non-product: pre-fill unitCost in nonProductLots[0]
      const cost = (selectedItems[0].unit_cost || selectedItems[0].cost || '').toString();
      setFormData(prev => {
        const existingCost = prev.nonProductLots?.[0]?.unitCost;
        if (existingCost) return prev; // already set
        return {
          ...prev,
          unitCost: cost, // keep for backwards compat
          nonProductLots: [{ ...(prev.nonProductLots?.[0] || {}), unitCost: cost }]
        };
      });
    } else if (selectedItems.length === 1 && !formData.unitCost) {
      // Single product: fill formData.unitCost (existing behaviour)
      const unitCost = selectedItems[0].unit_cost || selectedItems[0].cost || '';
      setFormData(prev => ({ ...prev, unitCost: unitCost.toString() }));
    } else if (selectedItems.length > 1 && !isProduct) {
      // Multi non-product: init lots per item with pre-filled unitCost
      const newItemsConfig = { ...itemsConfig };
      let updated = false;
      selectedItems.forEach(item => {
        if (!newItemsConfig[item.id]?.lots?.length) {
          const cost = (item.unit_cost || item.cost || '').toString();
          newItemsConfig[item.id] = {
            ...newItemsConfig[item.id],
            lots: [{ quantity: '', lotNumber: '', batchNumber: '', unitCost: cost, expiryDate: '' }]
          };
          updated = true;
        }
      });
      if (updated) setItemsConfig(newItemsConfig);
    }
  }, [selectedItems]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelectedItems([]);
      setSelectedLocation(null);
      setSelectedVariants([]);
      setFormData({
        quantity: '',
        lotNumber: '',
        batchNumber: '',
        vendor: null,
        invoiceNumber: '',
        invoiceDate: '',
        poNumber: '',
        poDate: '',
        unitCost: '',
        expiryDate: '',
        mfgDate: '',
        palletQty: '',
        caseQty: '',
        unitQty: '',
        shelfLife: '',
        notes: '',
        lots: [{ palletQty: '', caseQty: '', unitQty: '', lotNumber: '', batchNumber: '', mfgDate: '', expiryDate: '' }],
        nonProductLots: [{ quantity: '', lotNumber: '', batchNumber: '', unitCost: '', expiryDate: '' }]
      });
      setDocuments({});
      setItemsConfig({});
      setSubmitting(false);
    }
  }, [open]);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Step 0: products with variants require a variant selection
  const canProceedStep0 = (() => {
    if (selectedItems.length === 0 || !selectedLocation) return false;
    // Products: if single product has variants, must select one
    // (variant fetch happens in StepSelectItem — we can't check here if variants exist,
    //  but if onSelectVariant was called, selectedVariant will be set)
    return true;
  })();

  // Step 1 validation
  // Validate a list of lots — each lot must have its own unit config
  const validateLots = (lots) => {
    if (!lots || lots.length === 0) return false;
    return lots.every(lot => {
      if (!lot._configId) return false;
      const upc = parseInt(lot._unitsPerCase) || 1;
      const cpp = parseInt(lot._casesPerPallet) || 1;
      const pQty = parseInt(lot.palletQty) || 0;
      const cQty = parseInt(lot.caseQty) || 0;
      const uQty = parseInt(lot.unitQty) || 0;
      const totalBase = (pQty * cpp * upc) + (cQty * upc) + uQty;
      return totalBase > 0 && lot.lotNumber && lot.batchNumber && lot.mfgDate && lot.expiryDate;
    });
  };

  const canProceedStep1 = (() => {
    // Single item mode
    if (selectedItems.length === 1) {
      if (isProduct) {
        // Per-variant lots
        if (selectedVariants.length > 0) {
          const vl = formData.variantLots || {};
          return selectedVariants.every(v => validateLots(vl[v.id]));
        }
        // Flat lots (no variants)
        return validateLots(formData.lots);
      }
      // Non-product: validate each lot in nonProductLots
      const lots = formData.nonProductLots || [];
      return lots.length > 0 && lots.every(lot =>
        parseFloat(lot.quantity) > 0 &&
        lot.lotNumber && lot.batchNumber &&
        lot.unitCost !== undefined && lot.unitCost.toString().trim() !== '' && parseFloat(lot.unitCost) >= 0
      );
    }

    // Multi-item mode
    return selectedItems.every(item => {
      const cfg = itemsConfig[item.id];
      if (!cfg) return false;

      if (isProduct) {
        // Per-variant lots
        const variantIds = cfg.variantIds || (cfg.variantId ? [cfg.variantId] : []);
        if (variantIds.length > 0) {
          const vl = cfg.variantLots || {};
          return variantIds.every(vid => validateLots(vl[vid]));
        }
        // Flat lots
        return validateLots(cfg.lots);
      }

      // Non-product multi-item: validate per-item lots
      const lots = cfg.lots || [];
      return lots.length > 0 && lots.every(lot =>
        parseFloat(lot.quantity) > 0 &&
        lot.lotNumber && lot.batchNumber &&
        lot.unitCost !== undefined && lot.unitCost.toString().trim() !== '' && parseFloat(lot.unitCost) >= 0
      );
    });
  })();
  
  const canProceedStep2 = true; // Documents are optional

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Process each selected item - ONLY Add Stock
      const results = [];
      const isPerItemMode = selectedItems.length > 1 && Object.keys(itemsConfig).length > 0;
      
      for (const item of selectedItems) {
        // Use per-item config if available, otherwise use common formData
        const itemCfg = isPerItemMode ? itemsConfig[item.id] : null;
        const isProductMulti = isProduct && isPerItemMode;

        // Determine the submit function
        const addConfig = hasAddConfig(inventoryType) ? getAddConfig(inventoryType) : null;
        const submitFn  = addConfig?.customSubmitAdd || submitAddStock;

        if (isProductMulti) {
          // Product multi-item: supports per-variant lots or flat lots
          const variantIds = itemCfg?.variantIds || (itemCfg?.variantId ? [itemCfg.variantId] : []);
          const submitLot = async (lot, variantId) => {
            const shelfLife = (lot.mfgDate && lot.expiryDate)
              ? Math.round((new Date(lot.expiryDate) - new Date(lot.mfgDate)) / (1000 * 60 * 60 * 24 * 30.44))
              : null;
            const data = {
              item,
              location: selectedLocation,
              quantity: 0,
              lotNumber: lot.lotNumber,
              batchNumber: lot.batchNumber,
              vendor: null,
              invoiceNumber: null,
              invoiceDate: null,
              poNumber: null,
              poDate: null,
              unitCost: null,
              expiryDate: lot.expiryDate || null,
              mfgDate: lot.mfgDate || null,
              documents,
              notes: formData.notes,
              palletQty: lot.palletQty || '0',
              caseQty: lot.caseQty || '0',
              unitQty: lot.unitQty || '0',
              shelfLife,
              variantId: variantId || null,
              unitConfigId: lot._configId || null
            };
            return submitFn(companyId, inventoryType, config, data);
          };

          if (variantIds.length > 0 && itemCfg?.variantLots) {
            for (const vid of variantIds) {
              for (const lot of (itemCfg.variantLots[vid] || [])) {
                results.push(await submitLot(lot, vid));
              }
            }
          } else {
            for (const lot of (itemCfg?.lots || [])) {
              results.push(await submitLot(lot, null));
            }
          }
        } else if (isProduct) {
          // Single-item product: supports per-variant lots or flat lots
          const submitLot = async (lot, variantId) => {
            const shelfLife = (lot.mfgDate && lot.expiryDate)
              ? Math.round((new Date(lot.expiryDate) - new Date(lot.mfgDate)) / (1000 * 60 * 60 * 24 * 30.44))
              : null;
            const data = {
              item,
              location: selectedLocation,
              quantity: 0,
              lotNumber: lot.lotNumber,
              batchNumber: lot.batchNumber,
              vendor: null,
              invoiceNumber: null,
              invoiceDate: null,
              poNumber: null,
              poDate: null,
              unitCost: null,
              expiryDate: lot.expiryDate || null,
              mfgDate: lot.mfgDate || null,
              documents,
              notes: formData.notes,
              palletQty: lot.palletQty || '0',
              caseQty: lot.caseQty || '0',
              unitQty: lot.unitQty || '0',
              shelfLife,
              variantId: variantId || null,
              unitConfigId: lot._configId || null
            };
            return submitFn(companyId, inventoryType, config, data);
          };

          if (selectedVariants.length > 0 && formData.variantLots) {
            for (const variant of selectedVariants) {
              for (const lot of (formData.variantLots[variant.id] || [])) {
                results.push(await submitLot(lot, variant.id));
              }
            }
          } else if (formData.lots?.length > 0) {
            for (const lot of formData.lots) {
              results.push(await submitLot(lot, null));
            }
          }
        } else {
          // Non-product: iterate over per-item lots (multi) or nonProductLots (single)
          const lots = isPerItemMode
            ? (itemCfg?.lots || [])
            : (formData.nonProductLots || []);
          for (const lot of lots) {
            const data = {
              item,
              location: selectedLocation,
              quantity: parseFloat(lot.quantity),
              quantityMode: lot.quantityMode || 'container',
              lotNumber: lot.lotNumber,
              batchNumber: lot.batchNumber,
              vendor: formData.vendor,
              invoiceNumber: formData.invoiceNumber,
              invoiceDate: formData.invoiceDate,
              poNumber: formData.poNumber,
              poDate: formData.poDate,
              unitCost: lot.unitCost,
              expiryDate: lot.expiryDate || null,
              mfgDate: null,
              documents,
              notes: formData.notes,
              palletQty: '0',
              caseQty: '0',
              unitQty: '0',
              shelfLife: null,
              variantId: null,
              unitConfigId: null
            };
            results.push(await submitFn(companyId, inventoryType, config, data));
          }
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      if (failCount === 0) {
        onSuccess?.(`Successfully processed ${successCount} ${config.displayName.toLowerCase()}`);
        onClose();
      } else {
        onSuccess?.(`${successCount} succeeded, ${failCount} failed`, 'warning');
      }
    } catch (error) {
      console.error('Submit error:', error);
      onSuccess?.(`Error: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)',
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.success.main, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AddIcon sx={{ fontSize: 32, color: 'success.main' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>
              Add {config.displayName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Add new {config.displayName.toLowerCase()} to your system
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{
                color: theme.palette.text.secondary,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), transform: 'scale(1.05)' },
                transition: 'all 0.2s ease'
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.text.secondary,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 0, maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
        <Box sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 0: Select Item & Location */}
          <Step>
            <StepLabel>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {steps[0]}
              </Typography>
            </StepLabel>
            <StepContent>
              <StepSelectItem
                adjustmentType={adjustmentType}
                selectedItems={selectedItems}
                onSelectItems={setSelectedItems}
                selectedLocation={selectedLocation}
                onSelectLocation={setSelectedLocation}
                toLocation={null}
                onSelectToLocation={() => {}}
                items={items}
                locations={locations}
                config={config}
                companyId={companyId}
                selectedVariant={selectedVariants}
                onSelectVariant={setSelectedVariants}
              />
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceedStep0}
                  sx={{
                    borderRadius: 2,
                    px: 5,
                    py: 1.8,
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 1: Configure Quantities */}
          <Step>
            <StepLabel>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {steps[1]}
              </Typography>
            </StepLabel>
            <StepContent>
              {isProduct ? (
                <StepProductQuantity
                  formData={formData}
                  onFormChange={setFormData}
                  suppliers={suppliers}
                  config={config}
                  selectedItems={selectedItems}
                  selectedVariants={selectedVariants}
                  itemsConfig={itemsConfig}
                  onItemsConfigChange={setItemsConfig}
                  companyId={companyId}
                />
              ) : (
                <StepConfigureQuantity
                  adjustmentType={adjustmentType}
                  formData={formData}
                  onFormChange={setFormData}
                  suppliers={suppliers}
                  config={config}
                  selectedItems={selectedItems}
                  itemsConfig={itemsConfig}
                  onItemsConfigChange={setItemsConfig}
                />
              )}
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button 
                  onClick={handleBack} 
                  sx={{ 
                    borderRadius: 2,
                    px: 4,
                    py: 1.8,
                    fontSize: '1rem'
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceedStep1}
                  sx={{
                    borderRadius: 2,
                    px: 5,
                    py: 1.8,
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: Upload Documents */}
          <Step>
            <StepLabel>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {steps[2]}
              </Typography>
            </StepLabel>
            <StepContent>
              <StepUploadDocuments
                adjustmentType={adjustmentType}
                documents={documents}
                onDocumentsChange={setDocuments}
                config={config}
              />
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button 
                  onClick={handleBack} 
                  sx={{ 
                    borderRadius: 2,
                    px: 4,
                    py: 1.8,
                    fontSize: '1rem'
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceedStep2}
                  sx={{
                    borderRadius: 2,
                    px: 5,
                    py: 1.8,
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}
                >
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Review & Submit */}
          <Step>
            <StepLabel>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {steps[3]}
              </Typography>
            </StepLabel>
            <StepContent>
              <StepReview
                adjustmentType={adjustmentType}
                selectedItems={selectedItems}
                selectedLocation={selectedLocation}
                toLocation={null}
                formData={formData}
                config={config}
                itemsConfig={itemsConfig}
                selectedVariants={selectedVariants}
              />
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button 
                  onClick={handleBack} 
                  sx={{ 
                    borderRadius: 2,
                    px: 4,
                    py: 1.8,
                    fontSize: '1rem'
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={22} color="inherit" />}
                  sx={{
                    borderRadius: 2,
                    px: 5,
                    py: 1.8,
                    fontWeight: 500,
                    fontSize: '1rem',
                    bgcolor: theme.palette.success.main,
                    '&:hover': {
                      bgcolor: theme.palette.success.dark
                    }
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
        </Box>
      </Box>
    </Dialog>
  );
}
